import { useEffect, useMemo, useState } from 'react'
import { Pencil } from 'lucide-react'
import { getDoc, doc } from 'firebase/firestore'
import { db } from '../../core/firebase'
import { useNorseTerm } from '../norseterm/NorseTermData'
import { useRoster, seenLabel, toDate, useDirectory, saveStudent, deleteStudent, type StudentFix } from './model'
import { gradeFor } from '../../core/students'
import { useAuth } from '../../core/auth'
import type { StudentDoc } from '../../core/types'
import { Text } from '../../ui/Field'
import { Loading, Empty, ErrorState } from '../../ui/States'

/**
 * The student roster. Students are added by the Google Sheet sync; office and
 * admin (`canEdit`) can correct one student here: name, district ID, seminar
 * teacher, on or off. A later sync puts back whatever the Sheet says, so the
 * form says to change the Sheet too.
 */
export function RosterTab({ canEdit = false }: { canEdit?: boolean }) {
  const { rows, err } = useRoster(true)
  const [editing, setEditing] = useState<StudentDoc | null>(null)
  const [q, setQ] = useState('')
  const [grade, setGrade] = useState<'any' | number>('any')
  const [seminar, setSeminar] = useState('any')
  const [status, setStatus] = useState<'active' | 'off' | 'never' | 'any'>('active')

  const seminars = useMemo(() => [...new Map((rows ?? [])
    .map((r) => [r.seminarTeacherEmail, r.seminarTeacherName || r.seminarTeacherEmail])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1])), [rows])
  const grades = useMemo(() => [...new Set((rows ?? []).map((r) => gradeFor(r.classYear)))].sort((a, b) => b - a), [rows])

  const shown = useMemo(() => (rows ?? [])
    .filter((r) => !q || `${r.displayName} ${r.email} ${r.studentId}`.toLowerCase().includes(q.toLowerCase()))
    .filter((r) => grade === 'any' || gradeFor(r.classYear) === grade)
    .filter((r) => seminar === 'any' || r.seminarTeacherEmail === seminar)
    .filter((r) => status === 'any' || (status === 'active' && r.active) || (status === 'off' && !r.active)
      || (status === 'never' && r.active && !toDate(r.lastSeenAt)))
    .sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName)),
  [rows, q, grade, seminar, status])

  if (err) return <ErrorState body={`Could not load the student list. ${err}`} />
  if (!rows) return <Loading label="Loading students" />
  if (editing) return <StudentEditor student={editing} onDone={() => setEditing(null)} />

  const lastSync = rows.map((r) => toDate(r.syncedAt)).filter((d): d is Date => !!d)
    .sort((a, b) => b.getTime() - a.getTime())[0]
  const syncedBy = rows.find((r) => toDate(r.syncedAt)?.getTime() === lastSync?.getTime())?.syncedBy

  return (
    <>
      <div className="notice notice--info">
        <span aria-hidden="true">i</span>
        <div>
          Students come from the JMC upload{canEdit ? ' above' : ''}: upload a new export whenever the student list changes,
          and anyone no longer in it is turned off. {canEdit && <>To correct one student, click the pencil.</>} {lastSync
            ? <>Last updated {lastSync.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}{syncedBy ? ` by ${syncedBy}` : ''}.</>
            : 'It has not been synced yet.'}
        </div>
      </div>

      <div className="filters">
        <input className="opt__input" placeholder="Search name, email or student ID" value={q}
          aria-label="Search students" onChange={(e) => setQ(e.target.value)} />
        <select className="opt__input" aria-label="Grade" value={String(grade)}
          onChange={(e) => setGrade(e.target.value === 'any' ? 'any' : Number(e.target.value))}>
          <option value="any">Every grade</option>
          {grades.map((g) => <option key={g} value={g}>Grade {g}</option>)}
        </select>
        <select className="opt__input" aria-label="Seminar teacher" value={seminar} onChange={(e) => setSeminar(e.target.value)}>
          <option value="any">Every seminar</option>
          {seminars.map(([email, name]) => <option key={email} value={email}>{name}</option>)}
        </select>
        <select className="opt__input" aria-label="Status" value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="active">Active</option>
          <option value="never">Never signed in</option>
          <option value="off">Turned off</option>
          <option value="any">Everyone</option>
        </select>
      </div>

      <p className="opt__hint" style={{ marginTop: 0 }}>{shown.length} of {rows.length} students</p>

      {shown.length === 0 ? (
        <Empty title="Nobody matches" body={rows.length ? 'Try a different filter.' : 'Nobody has been synced from the Sheet yet.'} />
      ) : (
        <>
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Grade</th><th>Student ID</th><th>Seminar teacher</th><th>Last sign-in</th>{canEdit && <th><span className="visually-hidden">Edit</span></th>}</tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.email} className={r.active ? '' : 'is-off'}>
                  <td>
                    {canEdit
                      ? <button className="row-link" onClick={() => setEditing(r)}>{r.displayName}</button>
                      : <div style={{ fontWeight: 610 }}>{r.displayName}</div>}
                    <div className="work__sub">{r.email}</div>
                    {!r.active && <span className="pill pill--serious" style={{ marginTop: 4 }}>Turned off</span>}
                  </td>
                  <td>{gradeFor(r.classYear)}</td>
                  <td>{r.studentId}</td>
                  <td>{r.seminarTeacherName || r.seminarTeacherEmail}</td>
                  <td className={toDate(r.lastSeenAt) ? '' : 'is-muted'}>{seenLabel(r.lastSeenAt)}</td>
                  {canEdit && (
                    <td>
                      <button className="opt__icon" aria-label={`Edit ${r.displayName}`} title="Edit" onClick={() => setEditing(r)}>
                        <Pencil size={15} aria-hidden="true" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="work cards">
            {shown.map((r) => (
              <li key={r.email} className="work__row">
                <div className="work__main">
                  {canEdit
                    ? <button className="row-link" onClick={() => setEditing(r)}>{r.displayName}</button>
                    : <div style={{ fontWeight: 610 }}>{r.displayName}</div>}
                  <div className="work__sub">{r.email} · Grade {gradeFor(r.classYear)} · ID {r.studentId}</div>
                  <div className="work__status">Seminar: {r.seminarTeacherName || r.seminarTeacherEmail}</div>
                  <div className="work__sub">Last signed in: {seenLabel(r.lastSeenAt)}</div>
                </div>
                {!r.active && <span className="pill pill--serious">Off</span>}
                {canEdit && (
                  <button className="opt__icon" aria-label={`Edit ${r.displayName}`} onClick={() => setEditing(r)}>
                    <Pencil size={15} aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}

/** One student, corrected by office or admin. Email and class year are fixed: they are the sign-in and the grade. */
function StudentEditor({ student: st, onDone }: { student: StudentDoc; onDone: () => void }) {
  const { user } = useAuth()
  const { rows: staff } = useDirectory()
  const [f, setF] = useState<StudentFix>({
    firstName: st.firstName, lastName: st.lastName, displayName: st.displayName, studentId: st.studentId,
    seminarTeacherEmail: st.seminarTeacherEmail, seminarTeacherName: st.seminarTeacherName ?? '', active: st.active,
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const set = <K extends keyof StudentFix>(k: K, v: StudentFix[K]) => setF((x) => ({ ...x, [k]: v }))
  // The display name follows first and last unless someone has typed their own.
  const autoName = `${f.firstName} ${f.lastName}`.trim()
  const teachers = (staff ?? []).filter((x) => x.active !== false).sort((a, b) => a.displayName.localeCompare(b.displayName))
  const ok = f.firstName.trim() && f.lastName.trim() && f.displayName.trim() && f.studentId.trim() && f.seminarTeacherEmail

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    try { await saveStudent(st.email, f, (user?.email ?? '').toLowerCase()); onDone() }
    catch (x) { setErr(x instanceof Error ? x.message : String(x)); setBusy(false) }
  }

  return (
    <form className="card" onSubmit={save} style={{ marginTop: 'var(--s-2)' }}>
      <button type="button" className="backlink" onClick={onDone} style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}>
        ← Student list
      </button>
      <h2 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-1)' }}>{st.displayName}</h2>
      <p className="work__sub" style={{ margin: '0 0 var(--s-3)' }}>
        {st.email} · Grade {gradeFor(st.classYear)} (from the class year in their email, so it cannot change here)
      </p>
      <div className="notice notice--info">
        <span aria-hidden="true">i</span>
        <div>Once the student Sheet is syncing, it is the source of truth: make the same change there too, or the next sync will put it back.</div>
      </div>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      <div className="grid2">
        <Text label="First name" value={f.firstName} required
          onChange={(v) => setF((x) => ({ ...x, firstName: v, displayName: x.displayName === `${x.firstName} ${x.lastName}`.trim() ? `${v} ${x.lastName}`.trim() : x.displayName }))} />
        <Text label="Last name" value={f.lastName} required
          onChange={(v) => setF((x) => ({ ...x, lastName: v, displayName: x.displayName === `${x.firstName} ${x.lastName}`.trim() ? `${x.firstName} ${v}`.trim() : x.displayName }))} />
      </div>
      <Text label="Name as shown" value={f.displayName} onChange={(v) => set('displayName', v)} required
        help={f.displayName === autoName ? 'Follows first and last name. Change it for a preferred name.' : 'A preferred name. Rosters and attendance show this.'} />
      <Text label="District student ID" value={f.studentId} onChange={(v) => set('studentId', v)} required
        help="Placement matches students on this, so it must be their real ID." />
      <label className="field">
        <span style={{ display: 'block', fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink-2)', marginBottom: 'var(--s-1)' }}>Seminar teacher</span>
        <select className="opt__input" value={f.seminarTeacherEmail}
          onChange={(e) => {
            const t = teachers.find((x) => x.email === e.target.value)
            setF((x) => ({ ...x, seminarTeacherEmail: e.target.value, seminarTeacherName: t?.displayName ?? '' }))
          }}>
          {!teachers.some((x) => x.email === f.seminarTeacherEmail) && <option value={f.seminarTeacherEmail}>{f.seminarTeacherName || f.seminarTeacherEmail}</option>}
          {teachers.map((x) => <option key={x.email} value={x.email}>{x.displayName}</option>)}
        </select>
      </label>
      <label className="pick" style={{ marginTop: 'var(--s-3)' }}>
        <input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} />
        <span className="pick__label">On the student list (untick for a student who has left; nothing they did is deleted)</span>
      </label>
      <div className="opt__bar">
        <button className="btn" type="submit" disabled={busy || !ok}>{busy ? 'Saving…' : 'Save'}</button>
        <button className="btn btn--ghost" type="button" onClick={onDone} disabled={busy}>Cancel</button>
      </div>
      <DeleteStudent student={st} onDone={onDone} />
    </form>
  )
}

/**
 * Delete, for Admin, and only for a student with nothing attached this term
 * (no course selections, no project proposal). Anyone else is turned off
 * instead, so what they did still reads.
 */
function DeleteStudent({ student: st, onDone }: { student: StudentDoc; onDone: () => void }) {
  const { term, roles } = useNorseTerm()
  const [attached, setAttached] = useState<boolean | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    if (!roles.run) return
    const id = `${term.id}_${st.email}`
    Promise.all([getDoc(doc(db, 'ntSelections', id)), getDoc(doc(db, 'ntProposals', id))])
      .then(([a, b]) => setAttached(a.exists() || b.exists()))
      .catch(() => setAttached(true))
  }, [roles.run, term.id, st.email])
  if (!roles.run || attached === null) return null
  return (
    <section style={{ marginTop: 'var(--s-5)', paddingTop: 'var(--s-3)', borderTop: '1px solid var(--line)' }}>
      <h3 className="opt__label">Delete this student</h3>
      {attached ? (
        <p className="opt__hint" style={{ marginTop: 0 }}>
          {st.displayName} already has course selections or a project proposal, so they can&apos;t be deleted. Untick
          <strong> On the student list</strong> above instead: they can no longer sign in, and their records still make sense.
        </p>
      ) : confirming ? (
        <>
          {err && <ErrorState body={`Not deleted. ${err}`} />}
          <p className="opt__hint" style={{ marginTop: 0 }}>Delete {st.displayName} ({st.email}) from SailNorse? This can&apos;t be undone; a later JMC upload would add them back if they are in it.</p>
          <div className="opt__bar">
            <button type="button" className="btn" onClick={() => deleteStudent(st.email).then(onDone).catch((e) => setErr(e instanceof Error ? e.message : String(e)))}>
              Yes, delete {st.firstName}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setConfirming(false)}>Keep them</button>
          </div>
        </>
      ) : (
        <>
          <p className="opt__hint" style={{ marginTop: 0 }}>For a student added by mistake or no longer at the school. Nothing is attached to them yet.</p>
          <button type="button" className="btn btn--ghost" style={{ width: 'auto' }} onClick={() => setConfirming(true)}>Delete student…</button>
        </>
      )}
    </section>
  )
}
